import {
  IApplicationAnalyticsProperties,
  IAttribute,
  ILeaderboardPayload,
} from "@livelike/javascript";

export enum SoccerMatchStatus {
  FIXTURE = "fixture",
  PLAYING = "playing",
  PLAYED = "played",
  CANCELLED = "cancelled",
  SUSPENDED = "suspended",
  POSTPONED = "postponed",
  AWARDED = "awarded",
}

export enum SoccerMatchCommentaryEventType {
  ADDED_TIME = "added time",
  ATTEMPT_BLOCKED = "attempt blocked",
  ATTEMPT_SAVED = "attempt saved",
  CONTENTIOUS_REFEREE_DECISIONS = "contentious referee decisions",
  CORNER = "corner",
  END_PERIOD = "end", // Note: use with {period_id}
  END_DELAY = "end delay",
  FREE_KICK_LOST = "free kick lost",
  FREE_KICK_WON = "free kick won",
  GOAL = "goal",
  LINEUP = "lineup",
  MISS = "miss",
  OFFSIDE = "offside",
  OWN_GOAL = "own goal",
  PENALTY_GOAL = "penalty goal",
  PENALTY_LOST = "penalty lost",
  PENALTY_MISS = "penalty miss",
  PENALTY_SAVED = "penalty saved",
  PENALTY_WON = "penalty won",
  PLAYER_RETIRED = "player retired",
  POST = "post",
  POSTPONED = "postponed",
  RED_CARD = "red card",
  SECOND_YELLOW_RED_CARD = "second_yellow red card",
  START_PERIOD = "start",
  START_DELAY = "start delay",
  SUBSTITUTION = "substitution",
  VAR_CANCELLED_GOAL = "VAR cancelled goal",
  DELETED_AFTER_REVIEW = "deleted after review",
  YELLOW_CARD = "yellow card",
}

// Only the events we handle in the UI
export type SupportedSoccerCommentaryEvent =
  | SoccerMatchCommentaryEventType.ADDED_TIME
  | SoccerMatchCommentaryEventType.ATTEMPT_BLOCKED
  | SoccerMatchCommentaryEventType.ATTEMPT_SAVED
  | SoccerMatchCommentaryEventType.CORNER
  | SoccerMatchCommentaryEventType.END_PERIOD
  | SoccerMatchCommentaryEventType.END_DELAY
  | SoccerMatchCommentaryEventType.FREE_KICK_LOST
  | SoccerMatchCommentaryEventType.FREE_KICK_WON
  | SoccerMatchCommentaryEventType.YELLOW_CARD
  | SoccerMatchCommentaryEventType.RED_CARD
  | SoccerMatchCommentaryEventType.SECOND_YELLOW_RED_CARD
  | SoccerMatchCommentaryEventType.GOAL
  | SoccerMatchCommentaryEventType.LINEUP
  | SoccerMatchCommentaryEventType.MISS
  | SoccerMatchCommentaryEventType.OFFSIDE
  | SoccerMatchCommentaryEventType.OWN_GOAL
  | SoccerMatchCommentaryEventType.PENALTY_GOAL
  | SoccerMatchCommentaryEventType.PENALTY_LOST
  | SoccerMatchCommentaryEventType.PENALTY_MISS
  | SoccerMatchCommentaryEventType.PENALTY_SAVED
  | SoccerMatchCommentaryEventType.PENALTY_WON
  | SoccerMatchCommentaryEventType.PLAYER_RETIRED
  | SoccerMatchCommentaryEventType.POST
  | SoccerMatchCommentaryEventType.POSTPONED
  | SoccerMatchCommentaryEventType.START_PERIOD
  | SoccerMatchCommentaryEventType.START_DELAY
  | SoccerMatchCommentaryEventType.SUBSTITUTION
  | SoccerMatchCommentaryEventType.CONTENTIOUS_REFEREE_DECISIONS
  | SoccerMatchCommentaryEventType.VAR_CANCELLED_GOAL
  | SoccerMatchCommentaryEventType.DELETED_AFTER_REVIEW;

export enum SoccerMatchStatusLabel {
  BEFORE_MATCH = "BEFORE MATCH",
  LIVE = "LIVE",
  MATCH_OVER = "MATCH OVER",
}

export enum SoccerPlayerPosition {
  GOALKEEPER = "Goalkeeper",
  WING_BACK = "Wing Back",
  DEFENDER = "Defender",
  DEFENSIVE_MIDFIELDER = "Defensive Midfielder",
  ATTACKING_MIDFIELDER = "Attacking Midfielder",
  MIDFIELDER = "Midfielder",
  STRIKER = "Striker",
  SUBSTITUTE = "Substitute",
}

export interface ISoccerTeamLineupPlayer {
  playerId: string;
  firstName: string;
  lastName: string;
  shortFirstName: string;
  shortLastName: string;
  matchName: string;
  shirtNumber: number;
  position: SoccerPlayerPosition;
  positionSide: string;
  formationPlace: string;
  captain?: string;
}

export interface ISoccerTeamOfficial {
  id: string;
  firstName: string;
  lastName: string;
  shortFirstName: string;
  shortLastName: string;
  type: string;
}

export interface ISoccerTeamLineups {
  contestant: ISoccerMatchContestant;
  formationUsed: string;
  players: ISoccerTeamLineupPlayer[];
  teamOfficials: ISoccerTeamOfficial[];
}

export interface ISoccerMatchLineups {
  matchLineups: ISoccerTeamLineups[];
}

export enum SoccerMatchStatType {
  SHOTS = "shots",
  SHOTS_ON_TARGET = "shots_on_target",
  SHOTS_INSIDE_BOX = "shots_inside_box",
  BALL_POSSESSION = "ball_possession",
  TOTAL_PASS = "total_pass",
  ACCURATE_PASS = "accurate_pass",
  PASSING_ACCURACY = "passing_accuracy",
  FOULS = "fouls",
  YELLOW_CARD = "yellow_card",
  RED_CARD = "red_card",
  CORNERS = "corners",
  OFFSIDE = "offside",
  TOTAL_TACKLE = "total_tackle",
  WON_TACKLE = "won_tackle",
  TOTAL_CLEARANCE = "total_clearance",
  PENALTY_CONCEDED = "penalty_conceded",
  PENALTY_KICKS = "penalty_kicks",
  SAVES = "saves",
  INTERCEPTIONS = "interceptions",
  BLOCKED_SCORING_ATT = "blocked_scoring_att",
}

export interface ISoccerMatchStatDetail {
  fh: string;
  sh: string;
  efh: string;
  esh: string;
  type: SoccerMatchStatType;
  value: string;
}

export type IMatchStatsDetail = {
  [K in SoccerMatchStatType]?: ISoccerMatchStatDetail;
};

export interface ISoccerMatchStats {
  matchStats: {
    contestantId: string;
    formationUsed: string;
    stats: IMatchStatsDetail;
  }[];
}

export interface ISoccerStanding {}

export type SoccerStandingsDivisionType =
  | "total"
  | "home"
  | "away"
  | "form-total"
  | "form-home"
  | "form-away"
  | "half-time-total"
  | "half-time-home"
  | "half-time-away"
  | "attendance"
  | "over-under"
  | "relegation"
  | "championship";

export interface ISoccerStandingsStage {
  id: string;
  formatId: string;
  name: string;
  vertical: number;
  startDate: string;
  endDate: string;
  divisionType: SoccerStandingsDivisionType;
  ranks: ISoccerStandingsRank[];
}

export interface ISoccerStandingsRank {
  groupId: string;
  groupName: string;
  horizontal: number;
  teamRanks: ISoccerStandingsTeamRank[];
}

export interface ISoccerStandingsTeamRank {
  rank: number;
  rankStatus: string;
  rankId: string;
  lastRank: number;
  contestantId: string;
  contestantName: string;
  contestantShortName: string;
  contestantClubName: string;
  contestantCode: string;
  points: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesLost: number;
  matchesDrawn: number;
  goalsFor: number;
  goalsAgainst: number;
  goaldifference: string;
  lastSix: string;
}

export interface ISoccerStandings {
  standings: {
    stages: ISoccerStandingsStage[];
  };
}

// Match Preview (MA8)
export interface ISoccerMatchPreviewH2HMatch {
  homeTeamName: string;
  homeTeamCode: string;
  awayTeamName: string;
  awayTeamCode: string;
  homeScore: number;
  awayScore: number;
  date: string;
  competitionName?: string;
}

export interface ISoccerMatchPreviewH2H {
  homeContestantWins: number;
  awayContestantWins: number;
  draws: number;
  matches: ISoccerMatchPreviewH2HMatch[];
}

export interface ISoccerMatchPreviewTeamForm {
  contestantId: string;
  lastSix: string;
  goalsFor: number;
  matchesPlayed: number;
}

export interface ISoccerMatchPreview {
  form: ISoccerMatchPreviewTeamForm[];
  previousMeetings: ISoccerMatchPreviewH2H;
  previousMeetingsAnyComp: ISoccerMatchPreviewH2H;
}

export type SoccerDataProvider = "sdapi";

export interface ISoccerMatchContestant {
  id: string;
  name: string;
  shortName: string;
  code: string;
  officialName?: string;
  position?: string;
  country?: {
    id: string;
    name: string;
  };
}

export interface ISoccerMatchStage {
  id: string;
  name: string;
  formatId?: string;
  startDate?: string;
  endDate?: string;
}

export interface ISoccerMatchSeries {
  id: string;
  name: string;
  formatId?: string;
}

export interface ISoccerCompetition {
  id: string;
  name: string;
  knownName: string;
  competitionCode: string;
  competitionFormat: string;
  country: {
    id: string;
    name: string;
  };
}

export interface ISoccerTournament {
  id: string;
  startDate: string;
  endDate: string;
  name: string;
}

export interface ISoccerMatchVenue {
  id: string;
  neutral: string;
  longName: string;
  shortName: string;
  name: string;
  city: string;
  countryId: string;
  country: string;
  countryCode: string;
}

export enum SoccerMatchEventType {
  GOAL = "goal",
  MISSED_PENALTY = "missedPen",
  CARD = "card",
  SUSTITUTE = "substitute",
  PENALTY_SHOT = "penaltyShot",
  PENALTY = "PENALTY",
}

export type ISoccerMatchEventsWithCount =
  | (ISoccerMatchEvent & { count: number })
  | ISoccerMatchEventWithPenaltyGroup;

export interface ISoccerMatchGoalEvent extends IBaseSoccerMatchEvent {
  contestantId: string;
  periodId: number;
  // timeMin: number;
  // timeMinSec: string;
  // lastUpdated: string;
  // timestamp: string;
  type: string;
  scorerId: string;
  scorerName: string;
  assistPlayerId: string;
  assistPlayerName: string;
  // optaEventId: string;
  homeScore: number;
  awayScore: number;
}

export interface ISoccerMatchMissedPenaltyEvent extends IBaseSoccerMatchEvent {
  contestantId: string;
  periodId: number;
  type: string;
  playerId: string;
  playerName: string;
  // optaEventId: string;
}

export interface ISoccerMatchPenaltyShotEvent extends IBaseSoccerMatchEvent {
  contestantId: string;
  outcome: string;
  playerId: string;
  scorerName: string;
  teamPenaltyNumber: number;
  // optaEventId: string;
}

export interface ISoccerMatchCardEvent extends IBaseSoccerMatchEvent {
  contestantId: string;
  periodId: number;
  type: string;
  playerId: string;
  playerName: string;
  // optaEventId: string;
  cardReason: string;
}

export interface ISoccerMatchSubstituteEvent extends IBaseSoccerMatchEvent {
  contestantId: string;
  periodId: number;
  playerOnId: string;
  playerOnName: string;
  playerOffId: string;
  playerOffName: string;
  subReason: string;
}

export interface ISoccerPenaltyEvent extends IBaseSoccerMatchEvent {
  count: number;
  subEvents: (ISoccerMatchPenaltyShotEvent | ISoccerMatchMissedPenaltyEvent)[];
}

export interface IBaseSoccerMatchEvent {
  eventType: SoccerMatchEventType;
  timeMin: number;
  timeMinSec: string;
  timestamp: string;
  // lastUpdated: string;
}

export type ISoccerMatchEvent =
  | ISoccerMatchGoalEvent
  | ISoccerMatchMissedPenaltyEvent
  | ISoccerMatchCardEvent
  | ISoccerMatchSubstituteEvent
  | ISoccerMatchPenaltyShotEvent;

export type ISoccerMatchEventWithPenaltyGroup = ISoccerMatchEvent &
  ISoccerPenaltyEvent;

export type ISoccerMatchGoalEventWithCount = ISoccerMatchGoalEvent & {
  count: number;
};
export type ISoccerMatchMissedPenaltyEventWithCount =
  ISoccerMatchMissedPenaltyEvent & { count: number };
export type ISoccerMatchPenaltyShotEventWithCount =
  ISoccerMatchPenaltyShotEvent & { count: number };

export interface ISoccerMatchScore {
  home: number;
  away: number;
}

export interface ISoccerMatchScores {
  ht: ISoccerMatchScore;
  ft: ISoccerMatchScore;
  et: ISoccerMatchScore;
  total: ISoccerMatchScore;
  pen: ISoccerMatchScore;
  // "aggregate": ISoccerMatchScore;
}

export interface IMatchDetails {
  matchLengthMin: number;
  matchLengthSec: number;
  matchTime?: number;
  periodId?: number;
}

export interface ISoccerMatch {
  id: string;
  matchDateTime: string;
  description?: string;
  week?: string;
  status: SoccerMatchStatus;
  winnerContestantId?: string;
  nextMatchWinnerId?: string;
  stage: ISoccerMatchStage;
  series?: ISoccerMatchSeries;
  contestants: ISoccerMatchContestant[];
  venue?: ISoccerMatchVenue;
  scores: ISoccerMatchScores;
  events: ISoccerMatchEvent[];
  matchDetails: IMatchDetails;
}

export interface ISoccerMatchListItem
  extends Omit<ISoccerMatch, "events" | "matchDetails"> {}

export interface ISoccerMatches {
  competition?: ISoccerCompetition;
  tournament?: ISoccerTournament;
  matches: ISoccerMatchListItem[];
}

export interface IStandingGroupTableColumn {
  headerLabel: string;
  propKey: keyof ISoccerStandingsTeamRank;
  headerClassName: string;
  valueClassName: string;
}

export interface IAppLogoConfig {
  name: string;
  translatedName: string;
  translatedAbbrName?: string;
  imgSrc: string;
  statsColor: {
    home: string;
    away: string;
  };
  textColor: {
    home: string;
    away: string;
  };
  otherName?: string;
}

export interface IFormationLayout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export enum PERIOD_ID_STATUS {
  FIRST_HALF = "First half",
  SECOND_HALF = "Second half",
  EXTRA_TIME_FIRST_HALF = "Extra time - first half",
  EXTRA_TIME_SECOND_HALF = "Extra time - second half",
  PENALTY_SHOOTOUT = "Penalty shootout",
  HALF_TIME = "Half time",
  END_OF_2ND_HALF_BEFORE_EXTRA = "End of second half - before extra time",
  EXTRA_TIME_HALF_TIME = "Extra time - half time",
  END_OF_EXTRA_TIME_BEFORE_PENALTIES = "End of extra half - before penalties",
  FULL_TIME = "Full time",
  PRE_MATCH = "Pre-match",
}

export const PERIOD_ID = {
  1: PERIOD_ID_STATUS.FIRST_HALF,
  2: PERIOD_ID_STATUS.SECOND_HALF,
  3: PERIOD_ID_STATUS.EXTRA_TIME_FIRST_HALF,
  4: PERIOD_ID_STATUS.EXTRA_TIME_SECOND_HALF,
  5: PERIOD_ID_STATUS.PENALTY_SHOOTOUT,
  10: PERIOD_ID_STATUS.HALF_TIME,
  11: PERIOD_ID_STATUS.END_OF_2ND_HALF_BEFORE_EXTRA,
  12: PERIOD_ID_STATUS.EXTRA_TIME_HALF_TIME,
  13: PERIOD_ID_STATUS.END_OF_EXTRA_TIME_BEFORE_PENALTIES,
  14: PERIOD_ID_STATUS.FULL_TIME,
  16: PERIOD_ID_STATUS.PRE_MATCH,
};

export type ProgramResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: IProgramResults[];
};

export interface IProgramResults {
  attributes: IAttribute[];
  active_sessions_statistics_url: string;
  analytics_properties: IApplicationAnalyticsProperties;
  application_url: string;
  ban_url: string;
  banned_chat_users_url: string;
  chat_enabled: boolean;
  chat_rooms: [];
  client_id: string;
  created_at: string | undefined;
  current_active_session_count: number;
  custom_id: string | null;
  delete_widgets_url: string;
  id: string;
  leaderboard_url: string;
  leaderboards: ILeaderboardPayload[];
  moderation_url: string;
  oembed_url: string;
  pubnub_enabled: boolean;
  queue_order_url: string;
  rank_url: string;
  reaction_packs_url: string;
  report_url: string;
  reward_items: [];
  rewards_type: string;
  scheduled_at: string;
  sendbird_channel: null;
  sponsors: [];
  sponsors_url: string;
  start_program_url: string;
  started_at: string;
  statistics_channel: string;
  status: string;
  sticker_packs_url: string;
  stop_program_url: string;
  stopped_at: null;
  stream_url: string;
  subscribe_channel: string;
  sync_channel: null;
  sync_sessions_url: string;
  timeline_url: string;
  title: string;
  unclaimed_widget_interactions_url_template: string;
  update_sticker_packs_url: string;
  url: string;
  widget_count: number;
  widget_interactions_url_template: string;
  widget_reaction_packs_url: string;
  widgets_enabled: boolean;
  widgets_url: string;
  // LiveLike returns custom_data as a JSON-encoded string (or null).
  custom_data: string | null;
}

export enum VisitType {
  FirstVisit = "firstVisit",
  SecondVisit = "secondVisit",
  MultiVisit = "multiVisit",
}
